import {
    PropsWithChildren,
    createContext,
    useContext,
    useState
} from 'react';

import { Conversation, Message } from './DataDefinitions';
import apiClient, { handleAPIError } from 'helpers/apiService';
import { useAuth } from 'providers/AuthProvider'

type ConversationFilterType = 'all' | 'read' | 'unread';

interface ChatProviderInterface {
    conversations: Conversation[];
}

interface ChatState {
    conversations: Conversation[];
    currentConversation: null | Conversation;
    filterBy: ConversationFilterType;
    showConversationDetails: boolean;
    showUserListOffcanvas: boolean;
}

interface ChatContextInterface extends ChatState {
    // chatState: ChatState;
    startNewChat: () => void;
    setCurrentConversation: (selectedConversation: Conversation) => void;
    sendMessage: (prompt: string) => void;
    deleteConversation: (conversation_id: number) => void;
}

export const AIChatContext = createContext({} as ChatContextInterface);

const AIChatProvider = ({
    children,
    conversations
}: PropsWithChildren<ChatProviderInterface>) => {
    const initState: ChatState = {
        conversations: conversations,
        currentConversation: null,
        filterBy: 'all',
        showUserListOffcanvas: false,
        showConversationDetails: false
    };

    const auth = useAuth();
    const [chatState, setChatState] = useState<ChatState>(initState);

    const startNewChat = () => {
        const newConversation: Conversation = {
            conversation_id: 0,
            title: "new conversation",
            messages: [],
            inserted_at: new Date().toISOString() // Inserted date in ISO format
        };


        setChatState((prevState) => {
            // Check if a conversation with id=0 already exists
            const existingConversation = prevState.conversations.find(convo => convo.conversation_id === 0);

            if (existingConversation) {
                // If the conversation with id=0 exists, update currentConversation but do not add to conversations array
                return {
                    ...prevState,
                    currentConversation: existingConversation,
                };
            } else {
                // If it doesn't exist, add newConversation to conversations array and set as currentConversation
                return {
                    ...prevState,
                    currentConversation: newConversation,
                    conversations: [newConversation, ...prevState.conversations],
                };
            }
        });
    };


    const setCurrentConversation = async (selectedConversation: Conversation) => {
        if (selectedConversation) {
            try {
                const response = await apiClient.get(
                    `/v1/va/${selectedConversation.conversation_id}`,
                    {
                        headers: {
                            'accept': 'application/json',

                        },
                        withCredentials: true,
                    }
                );
                selectedConversation.messages = response.data;
            } catch (error) {
                console.error('Error fetching data:', error);
            }

            setChatState((prevState) => ({
                ...prevState, // Keep the rest of the state unchanged
                currentConversation: selectedConversation,
            }));
        }

    };

    /*
    const getMaxMessageId = (messages: Message[]): number => {
        return messages.reduce((maxId, message) => {
            return message.id > maxId ? message.id : maxId;
        }, 0); // Start with 0 as the initial maxId
    };*/

    const addEmptyMessageToCurrentConversation = (prompt: string, callback?: (lastId: number) => void) => {
        setChatState((prevState) => {
            if (!prevState.currentConversation) {
                return prevState; // If there is no current conversation, do nothing
            }

            const newMessageUser: Message = {
                type: 'sent',
                time: new Date().toISOString(), // Current time in ISO format
                message: prompt, // Empty message content
                asking: false,
            };

            const newMessageAI: Message = {
                type: 'received',
                time: new Date().toISOString(), // Current time in ISO format
                message: "", // Empty message content
                asking: true,
            };

            const updatedConversation = {
                ...prevState.currentConversation,
                messages: [
                    ...prevState.currentConversation.messages, // Keep existing messages
                    newMessageUser,
                    newMessageAI // Add an empty message
                ]
            };

            return {
                ...prevState,
                currentConversation: updatedConversation,   // is updated with the new message
                conversations: prevState.conversations.map(convo => // array is updated by replacing the modified currentConversation in the list
                    convo.conversation_id === updatedConversation.conversation_id ? updatedConversation : convo
                ),
            };
        });
    };


    const updateMessageWithApiResponse = (message: string) => {
        setChatState((prevState) => {
            if (!prevState.currentConversation) {
                return prevState; // If there is no current conversation, do nothing
            }

            const updatedMessages = prevState.currentConversation.messages.map((msg) => {
                if (msg.asking === true) {
                    return {
                        ...msg,
                        message: message, // Set the new message from the API response
                        asking: false,
                    };
                }
                return msg; // Return the message unchanged if it's not id=0
            });

            const updatedConversation: Conversation = {
                ...prevState.currentConversation,
                messages: updatedMessages, // Use the updated messages array
            };

            return {
                ...prevState,
                currentConversation: updatedConversation,
                conversations: prevState.conversations.map(convo =>
                    convo.conversation_id === updatedConversation.conversation_id ? updatedConversation : convo
                ),
            };
        });
    };

    const updateCurrentConversation = (newTitle: string, newId: number) => {
        setChatState((prevState) => {
            if (!prevState.currentConversation) {
                return prevState; // Do nothing if there is no current conversation
            }

            // Update the current conversation with the new title and id
            const updatedConversation: Conversation = {
                ...prevState.currentConversation,
                title: newTitle, // Set the new title
                conversation_id: newId,       // Set the new id
            };

            return {
                ...prevState,
                currentConversation: updatedConversation,
                // Update the conversations array with the updated currentConversation
                conversations: prevState.conversations.map(convo =>
                    convo.conversation_id === prevState.currentConversation?.conversation_id ? updatedConversation : convo
                ),
            };
        });
    };


    const sendMessage = async (prompt: string) => {

        addEmptyMessageToCurrentConversation(prompt);

        const formData = new FormData();
        formData.append('questionarieForm', JSON.stringify({ 'prompt': prompt, 'conversation': chatState.currentConversation }));
        try {
            const response = await apiClient.post(
                `/v1/va/prompt`, formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',

                    }
                }
            );

            updateMessageWithApiResponse(response.data['message']);
            //if (messageId === 1)
            if (response.data['update_conversation_on_FE'] === true) {
                updateCurrentConversation(prompt, response.data['conversation_id'])
            }

        } catch (error) {
            console.error('Request failed:', error);

        }
    }

    const deleteConversation = async (conversation_id: number) => {
        try {
            const response = await apiClient.delete(
                `/v1/va/${conversation_id}`,
                {
                    headers: {
                        'accept': 'application/json',

                    }
                }
            );


            setChatState((prevState) => ({
                ...prevState, // Keep the rest of the state unchanged
                conversations: response.data,
            }));
        } catch (error) {
            console.error('Request failed:', error);

        }
    }


    return (
        <AIChatContext.Provider
            value={{
                ...chatState,
                startNewChat,
                setCurrentConversation,
                deleteConversation,
                sendMessage,
            }}
        >
            {children}
        </AIChatContext.Provider>
    );
};

export const useChatContext = () => useContext(AIChatContext);

export default AIChatProvider;
